import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Loader2, AlertCircle, PartyPopper, Calendar, Clock, User, Stethoscope } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to format time to AM/PM
const formatTimeToAMPM = (time24) => {
  if (!time24 || time24 === 'queue') return time24;
  
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const PatientConfirmPage = () => {
  const { appointmentId, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [alreadyActioned, setAlreadyActioned] = useState(false);
  const [actionedStatus, setActionedStatus] = useState('');
  const [appointmentDetails, setAppointmentDetails] = useState(null);

  useEffect(() => {
    const confirmAppointment = async () => {
      try {
        // First check if already actioned
        const checkRes = await axios.get(`${API_URL}/api/patient/appointment/${appointmentId}/${token}`);
        
        if (checkRes.data.already_actioned) {
          setAlreadyActioned(true);
          setActionedStatus(checkRes.data.status);
          setLoading(false);
          return;
        }

        setAppointmentDetails(checkRes.data.appointment);

        // Auto-confirm the appointment
        const res = await axios.post(`${API_URL}/api/patient/confirm/${appointmentId}/${token}`);
        
        if (res.data.success) {
          setSuccess(true);
        } else {
          setError(res.data.message);
        }
      } catch (err) {
        console.error('Error confirming appointment:', err);
        setError(err.response?.data?.detail || 'Invalid or expired link. Please contact the clinic.');
      } finally {
        setLoading(false);
      }
    };

    confirmAppointment();
  }, [appointmentId, token]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Confirming your appointment...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Oops!</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already actioned state
  if (alreadyActioned) {
    const statusConfig = {
      confirmed: { icon: CheckCircle, color: 'text-green-500', bg: 'from-green-50 to-emerald-50', message: 'Your appointment is already confirmed!' },
      cancelled: { icon: AlertCircle, color: 'text-red-500', bg: 'from-red-50 to-orange-50', message: 'This appointment has been cancelled.' },
      rescheduled: { icon: Calendar, color: 'text-blue-500', bg: 'from-blue-50 to-indigo-50', message: 'This appointment has been rescheduled.' }
    };
    const config = statusConfig[actionedStatus] || statusConfig.confirmed;
    const Icon = config.icon;

    return (
      <div className={`min-h-screen bg-gradient-to-br ${config.bg} flex items-center justify-center p-4`}>
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Icon className={`w-16 h-16 ${config.color} mx-auto mb-4`} />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Already {actionedStatus}</h2>
            <p className="text-gray-600">{config.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <PartyPopper className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-6">Your appointment has been confirmed.</p>
          
          {appointmentDetails && (
            <div className="bg-green-50 rounded-lg p-4 text-left space-y-2 mb-6">
              <div className="flex items-center gap-2 text-gray-700">
                <User className="w-4 h-4 text-green-600" />
                <span className="font-medium">{appointmentDetails.patient_name}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <Stethoscope className="w-4 h-4 text-green-600" />
                <span>{appointmentDetails.service_name}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <Calendar className="w-4 h-4 text-green-600" />
                <span>{new Date(appointmentDetails.scheduled_date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
              </div>
              {appointmentDetails.scheduled_time && (
                <div className="flex items-center gap-2 text-gray-700">
                  <Clock className="w-4 h-4 text-green-600" />
                  <span>{formatTimeToAMPM(appointmentDetails.scheduled_time)}</span>
                </div>
              )}
            </div>
          )}

          <div className="p-4 bg-gray-100 rounded-lg">
            <p className="text-sm text-gray-600">
              We look forward to seeing you! You can close this window now.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PatientConfirmPage;
